import React from "react";
import toast from "react-hot-toast";
import {
    Alert, AlertIcon, Box, Icon, IconButton, Input, InputGroup, InputRightElement, Link, Stack, Text, Tooltip,
} from "@chakra-ui/react";
import { MdContentCopy, MdOpenInNew } from "react-icons/md";

/**
 * Copies a string to clipboard with toast feedback.
 * @param {string} value - Value to copy
 */
async function copyToClipboard(value) {
    try {
        await navigator.clipboard.writeText(value);
        toast.success("Lien copié");
    } catch (e) {
        toast.error("Impossible de copier le lien");
    }
}

/**
 * Renders the success banner shown above the URL field.
 * @param {Object} props - { result }
 * @returns {React.ReactElement}
 */
function SuccessBanner({ result }) {
    return (
        <Alert status="success" borderRadius="12px" alignItems="flex-start">
            <AlertIcon />
            <Box>
                <Text fontSize="0.78rem" fontWeight="600">Lien généré avec succès</Text>
                <Text fontSize="0.72rem" mt="2px">
                    Référence : {result?.reference || "—"} · Statut : {result?.status || "pending"}
                </Text>
            </Box>
        </Alert>
    );
}

/**
 * Renders the copy + open-in-new-tab action buttons inside the URL field.
 * @param {Object} props - { url }
 * @returns {React.ReactElement}
 */
function UrlActions({ url }) {
    return (
        <InputRightElement width="80px">
            <Tooltip label="Copier" hasArrow>
                <IconButton aria-label="Copier" size="sm" variant="ghost" colorScheme="brand"
                    icon={<Icon as={MdContentCopy} />} onClick={() => copyToClipboard(url)} />
            </Tooltip>
            <Tooltip label="Ouvrir dans un onglet" hasArrow>
                <IconButton as={Link} href={url} isExternal aria-label="Ouvrir" size="sm" variant="ghost"
                    colorScheme="brand" icon={<Icon as={MdOpenInNew} />} />
            </Tooltip>
        </InputRightElement>
    );
}

/**
 * Success panel displayed once a payment link has been generated.
 * Shows the reference, status banner, and the Stripe Checkout URL with copy/open actions.
 * @param {Object} props - { result }
 * @returns {React.ReactElement}
 */
function PaymentLinkSuccessPanel({ result }) {
    const url = result?.payment_link_url || "";
    return (
        <Stack spacing="16px">
            <SuccessBanner result={result} />
            <Box>
                <Text fontSize="sm" fontWeight="600" mb="4px">URL de paiement Stripe</Text>
                <InputGroup>
                    <Input value={url} isReadOnly borderRadius="12px" pr="84px" />
                    <UrlActions url={url} />
                </InputGroup>
            </Box>
        </Stack>
    );
}

export default PaymentLinkSuccessPanel;
